/**
 * @file index.tsx
 *
 * @fileoverview Renders blog detail page.
 */

import { GetServerSideProps, NextPage } from 'next';
import initApollo from '~/jobiqo/apollo/initApollo';
import Blog, { BlogProps } from '~/jobiqo/components/Blog';
import { getCleanUrlPart } from '~/jobiqo/components/Search/utils/searchHelpers';
import { blogDetailQuery } from '~/jobiqo/pages/blog/graphql/queries/blogDetail';
import { getOriginalLink } from '~/jobiqo/utils/links';
import { isRedirectCode, isPermanentRedirectCode } from '~/jobiqo/lib/redirect';
import RELATED_BLOGS from '~/jobiqo/pages/blog/graphql/queries/relatedBlogs.gql';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';

const BlogsPage: NextPage<BlogProps> = (props) => <Blog {...props} />;

export const getServerSideProps: GetServerSideProps = async function (ctx) {
  const frontendConfig = getServerFrontendConfig(ctx);

  if (!frontendConfig.APP.BLOG_FEATURE_ACTIVE) {
    return {
      props: {},
      redirect: {
        destination: '/404',
      },
    };
  }

  const apolloClient = initApollo({}, ctx);
  const path = getCleanUrlPart(getOriginalLink(ctx.resolvedUrl, ctx.locale));

  const { error, data, loading } = await apolloClient.query({
    query: blogDetailQuery(frontendConfig.APP),
    variables: {
      path,
      language: ctx.locale,
    },
  });

  if (data?.route?.code === 403) {
    return {
      props: {},
      redirect: {
        destination: '/403',
      },
    };
  }

  if (data?.route?.code === 404) {
    return {
      notFound: true,
    };
  }

  if (isRedirectCode(data?.route?.code) && path !== data?.route?.target) {
    return {
      redirect: {
        destination: data.route.target,
        permanent: isPermanentRedirectCode(data?.route?.code),
      },
      props: {},
    };
  }

  const relatedBlogs = await apolloClient.query({
    query: RELATED_BLOGS,
    variables: {
      blogId: data?.route?.content?.id,
    },
    skip: !data?.route?.content?.id,
  });

  return {
    props: {
      relatedBlogs: relatedBlogs.data?.relatedBlogs[0]?.items ?? [],
      data: data.route,
      error: !!error,
      loading: loading || relatedBlogs.loading,
    },
  };
};

export default BlogsPage;
